import React, { useState, useRef, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import BookletView from './BookletView';

const { FiX, FiPrinter, FiEye } = FiIcons;

const PAPER_SIZES = [
  { value: 'A5', label: 'A5 (148 × 210 mm)' },
  { value: 'A4', label: 'A4 (210 × 297 mm)' },
  { value: 'Letter', label: 'US Letter (8.5 × 11 in)' },
];

const MARGINS = [
  { value: '10mm', label: 'Narrow (10 mm)' },
  { value: '14mm', label: 'Normal (14 mm)' },
  { value: '20mm', label: 'Wide (20 mm)' },
];

const FONT_SCALES = [
  { value: 'normal', label: 'Normal' },
  { value: 'large', label: 'Large' },
  { value: 'xlarge', label: 'X-Large' },
];

const ProgrammePrintExport = ({ programme, items, library, showAudio, onClose }) => {
  const [paperSize, setPaperSize] = useState('A5');
  const [margin, setMargin] = useState('14mm');
  const [fontScale, setFontScale] = useState('normal');
  const [includeCover, setIncludeCover] = useState(true);
  const [includeCredits, setIncludeCredits] = useState(true);
  const styleRef = useRef(null);

  const doPrint = () => {
    document.body.dataset.progScale = fontScale;
    if (!includeCover) document.body.dataset.progHideCover = '1';
    else delete document.body.dataset.progHideCover;
    if (!includeCredits) document.body.dataset.progHideCredits = '1';
    else delete document.body.dataset.progHideCredits;

    if (styleRef.current) styleRef.current.remove();
    const style = document.createElement('style');
    style.id = 'prog-dynamic-print';
    style.textContent = `@media print { @page { size: ${paperSize}; margin: ${margin}; } }`;
    document.head.appendChild(style);
    styleRef.current = style;

    requestAnimationFrame(() => {
      window.print();
      setTimeout(() => {
        if (styleRef.current) { styleRef.current.remove(); styleRef.current = null; }
        delete document.body.dataset.progScale;
        delete document.body.dataset.progHideCover;
        delete document.body.dataset.progHideCredits;
      }, 600);
    });
  };

  useEffect(() => () => {
    if (styleRef.current) styleRef.current.remove();
    delete document.body.dataset.progScale;
    delete document.body.dataset.progHideCover;
    delete document.body.dataset.progHideCredits;
  }, []);

  const previewLarge = fontScale !== 'normal';
  const previewScale = fontScale === 'large' ? '1.05' : fontScale === 'xlarge' ? '1.12' : '1';

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-5xl bg-brand-light shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 bg-white border-b border-brand-dark shrink-0">
          <div>
            <h2 className="font-serif text-xl font-bold text-accent-navy">Print / Export</h2>
            <p className="text-xs text-text-muted mt-0.5">Configure and print the service programme booklet.</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-brand-light">
            <SafeIcon icon={FiX} className="w-5 h-5 text-text-muted" />
          </button>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col lg:flex-row">
          <div className="lg:w-72 shrink-0 bg-white border-r border-brand-dark p-5 space-y-5 overflow-y-auto">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-accent-navy mb-2">Paper size</p>
              <div className="space-y-1">
                {PAPER_SIZES.map(size => (
                  <label key={size.value} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="paper" checked={paperSize === size.value} onChange={() => setPaperSize(size.value)} className="accent-accent-gold" />
                    {size.label}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-accent-navy mb-2">Margins</p>
              <div className="space-y-1">
                {MARGINS.map(marginOption => (
                  <label key={marginOption.value} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="margin" checked={margin === marginOption.value} onChange={() => setMargin(marginOption.value)} className="accent-accent-gold" />
                    {marginOption.label}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-accent-navy mb-2">Text size</p>
              <div className="flex gap-2">
                {FONT_SCALES.map(scale => (
                  <button key={scale.value} type="button" onClick={() => setFontScale(scale.value)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-bold border ${fontScale === scale.value ? 'bg-accent-navy text-white border-accent-navy' : 'bg-white text-text-main border-brand-dark'}`}>
                    {scale.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-accent-navy mb-2">Pages to include</p>
              <label className="flex items-center gap-2 text-sm cursor-pointer mb-1">
                <input type="checkbox" checked={includeCover} onChange={event => setIncludeCover(event.target.checked)} className="accent-accent-gold" />
                Cover page
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={includeCredits} onChange={event => setIncludeCredits(event.target.checked)} className="accent-accent-gold" />
                Music credits page
              </label>
            </div>

            <div className="pt-3 border-t border-brand-dark">
              <button type="button" onClick={doPrint}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-accent-navy text-white text-sm font-bold hover:opacity-90">
                <SafeIcon icon={FiPrinter} className="w-4 h-4" />
                Print / Save as PDF
              </button>
              <p className="text-[11px] text-text-muted mt-2 text-center leading-relaxed">
                In the print dialog, choose &quot;Save as PDF&quot; as the destination to export a file.
              </p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6 bg-[#e8e2d5]">
            <div className="text-center mb-4">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-text-muted">
                <SafeIcon icon={FiEye} className="w-3.5 h-3.5" />
                Preview
              </span>
            </div>
            <div className="mx-auto max-w-sm origin-top" style={{ transform: `scale(${previewScale})`, filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.15))' }}>
              <div className={(!includeCover ? 'prog-cover-hidden ' : '') + (!includeCredits ? 'prog-credits-hidden' : '')}>
                <BookletView
                  programme={programme}
                  items={items}
                  library={library}
                  showAudio={showAudio}
                  large={previewLarge}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgrammePrintExport;